import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getPatientAssignments,
  putPatientAssignments,
  type PatientAssignmentSummary,
} from '../../../api/patients';
import { useAuth } from '../../auth/AuthProvider';

export function usePatientAssignments(patientId: string | undefined) {
  const { tokens } = useAuth();

  return useQuery<readonly PatientAssignmentSummary[]>({
    queryKey: ['patient-assignments', patientId],
    queryFn: () => getPatientAssignments(patientId!, tokens!.access_token),
    enabled: Boolean(patientId && tokens?.access_token),
  });
}

export function useUpdatePatientAssignments(patientId: string) {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (adminIds: readonly string[]) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return putPatientAssignments(patientId, adminIds, tokens.access_token);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['patient-assignments', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patient', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patients'] }),
        queryClient.invalidateQueries({ queryKey: ['chats'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
      ]);
    },
  });
}
